import type { TeamSlot } from './room';
import type { TeamConfig } from '../logic/team';
import type { GameSettings } from '../core/gameSettings';

/**
 * Wire protocol shared by the browser client and the game server.
 * Bump PROTOCOL_VERSION on breaking changes: the server rejects mismatched
 * clients with a "refresh required" error instead of desyncing silently.
 */
export const PROTOCOL_VERSION = 4;

export const ROOM_TYPE = 'vvorms';

/** Error codes used with Colyseus ServerError / close codes. */
export const ERR_PROTOCOL_MISMATCH = 4100;
export const ERR_GAME_IN_PROGRESS = 4101;
export const ERR_ROOM_FULL = 4102;
export const ERR_KICKED = 4103;

export interface JoinOptions {
  playerId: string;
  playerName: string;
  protocolVersion: number;
  /** Only used on room creation. */
  roomName?: string;
}

/** Authoritative lobby/room state broadcast by the server. */
export interface RoomStateSync {
  roomId: string;
  roomName: string;
  hostId: string;
  players: Array<{
    id: string;
    name: string;
    teamIds: string[];
    ready: boolean;
    isSpectator: boolean;
  }>;
  teamSlots: TeamSlot[];
  state: 'lobby' | 'playing' | 'finished';
  maxTeamsPerPlayer: number;
  allianceCount: number;
  seq: number;
  disconnectedPlayers: string[];
}

export interface GameStartBroadcast {
  terrainSeed: number;
  teams: TeamConfig[];
  settings?: GameSettings;
}

/**
 * Discrete player intents sent to the server-authoritative simulation.
 * The server validates that the sender owns the team whose turn it is.
 */
export type PlayerAction =
  | { type: 'input'; left: boolean; right: boolean; up: boolean; down: boolean }
  | { type: 'aim'; facing?: 'left' | 'right'; angle: number }
  | { type: 'jump' }
  | { type: 'backflip' }
  | { type: 'firePress' }
  | { type: 'fireRelease' }
  | { type: 'fireShot'; facing?: 'left' | 'right'; angleDeg: number; power: number }
  | { type: 'fireTarget'; x: number; y: number }
  | { type: 'lockTarget'; x: number; y: number }
  | {
      /**
       * On the sender's turn this changes the active weapon. Off turn,
       * `teamId` (a team the sender owns) pre-selects the weapon on the worm
       * that will act next for that team (per-worm weapon memory).
       */
      type: 'selectWeapon';
      weaponId: string;
      teamId?: string;
    }
  | { type: 'useItem'; itemId: string }
  | { type: 'cycleFuse' }
  | { type: 'girderConfig'; angleIndex: number; sizeIndex: number }
  | { type: 'ropeExtend'; delta: number }
  | { type: 'releaseRope' };

/**
 * Message names. Client -> server intents are validated server-side;
 * server -> client broadcasts are authoritative.
 */
export const MSG = {
  // server -> client
  roomState: 'room-state',
  gameStart: 'game-start',
  kicked: 'kicked',
  pong: 'pong',
  // client -> server (lobby intents)
  claimTeam: 'claim-team',
  releaseTeam: 'release-team',
  teamName: 'team-name',
  teamAlliance: 'team-alliance',
  tankType: 'tank-type',
  allianceCount: 'alliance-count',
  randomizeAlliances: 'randomize-alliances',
  spectator: 'spectator',
  maxTeams: 'max-teams',
  kick: 'kick',
  startGame: 'start-game',
  ping: 'ping',
  // game phase: server -> client snapshots/events; client -> server actions
  gameState: 'game-state',
  gameEvent: 'game-event',
  playerAction: 'player-action',
  requestKeyframe: 'request-keyframe',
} as const;
